import UPButton from 'components/button';
import UPButtonGroup from 'components/button/UPGroupButton';
import { UPToggleButton } from 'components/button/styles';
import ErrorBar from 'components/error-bar';
import GeneralConfirmModal from 'components/general-confirm-modal';
import UPSidePanel, { UPSidePanelSection } from 'components/side-panel';
import { useTranslation } from 'react-i18next';
import {
  FEATURE_ACCESS__BULK_CONFIRM,
  FEATURE_ACCESS__CONCURRENT_CHANGE,
  FEATURE_ACCESS__DISABLE,
  FEATURE_ACCESS__EDIT_ALL,
  FEATURE_ACCESS__ENABLE,
  FEATURE_ACCESS__LEAVE_UNCHANGED,
  FEATURE_ACCESS__SELECTED,
  GENERAL__CLOSE,
  GENERAL__RETRY,
  GENERAL__SAVE_BUTTON,
} from 'translations/constants';
import { FEATURE_LABEL_KEYS, FEATURES } from './constants';
import { UseFeatureAccessBulkResult } from './hooks/useFeatureAccessBulk';
import { PanelErrorRow, PanelFeatureRow } from './styles';
import { BulkOption } from './utils';

interface FeatureAccessBulkPanelProps {
  bulk: UseFeatureAccessBulkResult;
  selectedIds: number[];
  selectedCompanyNames: string[];
}

const OPTION_LABEL_KEYS: Record<BulkOption, string> = {
  enable: FEATURE_ACCESS__ENABLE,
  disable: FEATURE_ACCESS__DISABLE,
  unchanged: FEATURE_ACCESS__LEAVE_UNCHANGED,
};
const OPTIONS: BulkOption[] = ['enable', 'disable', 'unchanged'];

const FeatureAccessBulkPanel = ({
  bulk,
  selectedIds,
  selectedCompanyNames,
}: FeatureAccessBulkPanelProps): JSX.Element => {
  const [t] = useTranslation();

  return (
    <>
      <UPSidePanel
        open={bulk.isOpen}
        title={t(FEATURE_ACCESS__EDIT_ALL)}
        subtitle={`${selectedIds.length} ${t(FEATURE_ACCESS__SELECTED)}`}
        onClose={bulk.close}
        testId="feature-access-bulk-panel"
        footer={
          <>
            <UPButton
              text={t(GENERAL__SAVE_BUTTON)}
              onClick={bulk.requestSave}
              disabled={bulk.isSaving}
              testId="feature-access-bulk-save"
            />
            <UPButton text={t(GENERAL__CLOSE)} onClick={bulk.close} outlined testId="feature-access-bulk-close" />
          </>
        }
      >
        {bulk.saveErrorKey && (
          <PanelErrorRow>
            <ErrorBar message={t(bulk.saveErrorKey)} />
            {bulk.saveErrorKey !== FEATURE_ACCESS__CONCURRENT_CHANGE && (
              <UPButton
                text={t(GENERAL__RETRY)}
                onClick={() => bulk.confirm(selectedIds)}
                disabled={bulk.isSaving}
                outlined
                testId="feature-access-bulk-save-retry"
              />
            )}
          </PanelErrorRow>
        )}
        <UPSidePanelSection title={selectedCompanyNames.join(', ')}>
          {FEATURES.map(feature => (
            <PanelFeatureRow
              key={feature}
              sx={{ borderBottom: 'none', flexDirection: 'column', alignItems: 'flex-start', gap: '8px' }}
            >
              <span>{t(FEATURE_LABEL_KEYS[feature])}</span>
              <UPButtonGroup
                value={bulk.options[feature]}
                // MUI's exclusive group reports null when the pressed option is clicked again; keep the current value.
                onChange={value => value && bulk.setOption(feature, value as BulkOption)}
                disabled={bulk.isSaving}
              >
                {OPTIONS.map(option => (
                  <UPToggleButton
                    key={option}
                    value={option}
                    data-testid={`feature-access-bulk-option-${feature}-${option}`}
                  >
                    {t(OPTION_LABEL_KEYS[option])}
                  </UPToggleButton>
                ))}
              </UPButtonGroup>
            </PanelFeatureRow>
          ))}
        </UPSidePanelSection>
      </UPSidePanel>

      <GeneralConfirmModal
        open={bulk.isConfirmOpen}
        onClose={bulk.cancelConfirm}
        onAction={() => bulk.confirm(selectedIds)}
        content={t(FEATURE_ACCESS__BULK_CONFIRM, { total: selectedIds.length })}
        confirmTestId="feature-access-bulk-confirm"
        cancelTestId="feature-access-bulk-cancel"
        actionDisabled={bulk.isSaving}
      />
    </>
  );
};

export default FeatureAccessBulkPanel;
